import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import Konva from 'konva';
import {useAuth} from '../context/AuthProvider';
import { useSpotContext } from '../context/SpotProvider';
import { SpotMenu } from '../components/SpotMenu';
import PublicSellerMapMenu from '../components/PublicSellerMapMenu';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import { ACTIVITY_ID } from '../constants/activity';
import SellerInfoModal from '../components/SellerInfoModal';
import ConfirmationModal from '../components/ConfirmationModal';

function getViewportSize() {
    const viewport = window.visualViewport;

    return {
        width: Math.round(viewport?.width ?? window.innerWidth),
        height: Math.round(viewport?.height ?? window.innerHeight),
    };
}

var { width, height } = getViewportSize();
var scaleMultiplier = 0.8;
var lastDist = 0;
var lastCenter = null;

const COLOUR_FREE = "#66ff00";
const COLOUR_PRERESERVED = "orange";
const COLOUR_RESERVED = "red";
const COLOUR_PAYED = "blue";
const COLOUR_OWN = "#40E0D0";
const COLOUR_ELECTRIC_230 = "#f59e0b";
const COLOUR_ELECTRIC_400 = "#0f766e";
const SELLER_HIGHLIGHT_STROKE_WIDTH = 5;
const SELLER_HIGHLIGHT_PADDING = 18;
const SELLER_FOCUS_MAX_SCALE = 2.4;
const SELLER_FOCUS_SCALE_BOOST = 1.25;
const CAFE_MENU_IMAGE_SRC = '/kahvila_hinnasto.jpg';
// Coordinates converted from kartta.svg kahvio polygon to the same map pixel space as spot coordinates.
const CAFE_HOTSPOT_POINTS = [329.11, 318.00, 350.60, 348.00, 379.47, 325.35, 357.83, 296.25];
const CAFE_HOTSPOT_EXPAND_FACTOR = 0.98;
    const PUBLIC_EVENT_PROGRAM = [
        { time: '9:00', title: 'Markkinat auki' },
        { time: '10:00', title: 'Lauluyhtye Konkarit' },
        { time: '11:00', title: 'Lastenmusiikkiyhtye Rokki-Kokki' },
        { time: '11:45', title: 'LC Presidentin avauspuhe' },
        { time: '12:00', title: 'Vuoden Laurin julkistus' },
        { time: '12:15', title: 'SBS Wirmo F-Liiga N esittäytyy' },
        { time: '13:00', title: 'Lastenmusiikkiyhtye Rokki-Kokki' },
        { time: '14:00', title: 'Martat Neulekilpailun tulosjulkistus' },
        { time: '14:30', title: 'Markkina-Arpa -arvonta' },
        { time: '15:00', title: 'Markkinat päättyy' },
    ];

const scalePolygonFromCentroid = (points, factor = 1) => {
    if (!Array.isArray(points) || points.length < 6 || factor === 1) {
        return points;
    }

    let sumX = 0;
    let sumY = 0;
    const vertexCount = points.length / 2;

    for (let i = 0; i < points.length; i += 2) {
        sumX += points[i];
        sumY += points[i + 1];
    }

    const centerX = sumX / vertexCount;
    const centerY = sumY / vertexCount;

    return points.map((value, index) => {
        if (index % 2 === 0) {
            return Number((centerX + (value - centerX) * factor).toFixed(2));
        }

        return Number((centerY + (value - centerY) * factor).toFixed(2));
    });
};

var stage = null;
//resizeCanvas();
var layer;
var imageObj;
var layer2;
var textLayer;
var spotPoly = {};
var cafeHotspotShape = null;

/*

*/

//let selectedSpot;
let infoText;

const SPOT_STATUS_LOCKED = process.env.REACT_APP_SPOT_STATUS_LOCKED;
const SPOT_STATUS_FREE = process.env.REACT_APP_SPOT_STATUS_FREE;
const SPOT_STATUS_PREORDERED = process.env.REACT_APP_SPOT_STATUS_PREORDERED;
const SPOT_STATUS_ORDERED = process.env.REACT_APP_SPOT_STATUS_ORDERED;
const SPOT_STATUS_INVOICED = process.env.REACT_APP_SPOT_STATUS_INVOICED;
const SPOT_STATUS_PAYED = process.env.REACT_APP_SPOT_STATUS_PAYED;

//let handleClickOpen = null;
//let handleClose = null;

const emails = ['username@gmail.com', 'user02@gmail.com'];

const MapPage = () => {
    const [showSpots, setShowSpots] = useState('Kaikki'); 
    const navigate = useNavigate();
    const location = useLocation();
    const [sizeFilter, setSizeFilter] = useState([]);
    const [filteredSpots, setFilteredSpots] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [sellers, setSellers] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [isSellerModalOpen, setIsSellerModalOpen] = useState(false);
    const [selectedReservationSpotId, setSelectedReservationSpotId] = useState(null);
    const [isOwnReservationModalOpen, setIsOwnReservationModalOpen] = useState(false);
    const [isRemovingReservation, setIsRemovingReservation] = useState(false);
    const [isDeleteReservationConfirmOpen, setIsDeleteReservationConfirmOpen] = useState(false);
    const [isCafeModalOpen, setIsCafeModalOpen] = useState(false);
    const [isProgramModalOpen, setIsProgramModalOpen] = useState(false);
    const [isCafeImageError, setIsCafeImageError] = useState(false);
    const [cafeImageZoom, setCafeImageZoom] = useState(1);

    //const [selectElectricityDialogOpen, setSelectElectricityDialogOpen] = useState(false);
    //const [selectedValue, setSelectedValue] = useState(emails[1]);    
    const {spots, getOrders, lockSpot, unlockSpot} = useSpotContext();    

    const { isAuthenticated, isAdmin, auth } = useAuth();
    const api = useAxios();
    const apiRef = useRef(api);
    const hasFetchedSellersRef = useRef(false);
    const sellerFetchPromiseRef = useRef(null);
    const sellerHighlightAnimationRef = useRef(null);
    const sellerFocusFrameRef = useRef(null);
    const sellerFocusTimeoutRef = useRef(null);
    const viewportResizeFrameRef = useRef(null);
    const viewportResizeTimeoutRef = useRef(null);
    const highlightedSpotIdsRef = useRef([]);
    const userIsAdmin = Boolean(isAdmin?.() ?? auth?.isAdmin ?? auth?.user?.isAdmin);
    const electricityManagementMode = location.state?.electricityManagementMap === true && userIsAdmin;
    const showSpotInfoPanel = location.state?.hideSpotInfo !== true && !electricityManagementMode;
    const sellerMapView = location.state?.sellerMapView === true;
    const publicSellerMapMode = location.state?.publicSellerMap === true || sellerMapView;
    const routeSellerCompanyID = location.state?.sellerCompanyID;
    const routeSellerName = location.state?.sellerName ?? '';
    const routeSellerSpotNames = Array.isArray(location.state?.sellerSpotNames) ? location.state.sellerSpotNames : [];
    const routeFocusKey = location.key;
    const routeSelectedSellerId = routeSellerCompanyID != null
        ? `${routeSellerCompanyID}`
        : (routeSellerName ? `${routeSellerName}` : 'all');
    const [selectedPublicSellerId, setSelectedPublicSellerId] = useState(routeSelectedSellerId);
    const publicSellerMapModeRef = useRef(publicSellerMapMode);

    const normalizeSpotIdentifier = (value) => {
        const normalizedValue = `${value ?? ''}`.trim();
        const parsedValue = Number.parseInt(normalizedValue, 10);

        if (!Number.isNaN(parsedValue)) {
            return parsedValue.toString();
        }

        return normalizedValue.toLowerCase();
    }

    const normalizeElectricityType = (value) => String(value ?? '').trim().toLowerCase();

    const hasElectricityReservation = (spot) => ['230v', '400v'].includes(normalizeElectricityType(spot?.electricityType));

    const isReservedSpotStatus = (status) => {
        const normalizedStatus = String(status ?? '').trim().toLowerCase();

        return normalizedStatus === String(SPOT_STATUS_PREORDERED ?? '').trim().toLowerCase()
            || normalizedStatus === String(SPOT_STATUS_ORDERED ?? '').trim().toLowerCase()
            || normalizedStatus === String(SPOT_STATUS_INVOICED ?? '').trim().toLowerCase()
            || normalizedStatus === String(SPOT_STATUS_PAYED ?? '').trim().toLowerCase()
            || normalizedStatus === 'maksu paikalla'
            || normalizedStatus === 'ennakkovaraus'
            || normalizedStatus === 'varattu'
            || normalizedStatus === 'laskutettu'
            || normalizedStatus === 'maksettu';
    }

    const isElectricityManagementSpot = (spot) => isReservedSpotStatus(spot?.status) && hasElectricityReservation(spot);

    const getElectricityManagementColour = (spot) => {
        const electricityType = normalizeElectricityType(spot?.electricityType);

        if (electricityType === '400v') {
            return COLOUR_ELECTRIC_400;
        }

        if (electricityType === '230v') {
            return COLOUR_ELECTRIC_230;
        }

        return 'white';
    }

    const electricitySpotSummary = useMemo(() => {
        return (Array.isArray(spots) ? spots : []).reduce((summary, spot) => {
            if (!isElectricityManagementSpot(spot)) {
                return summary;
            }

            if (normalizeElectricityType(spot?.electricityType) === '400v') {
                summary.type400 += 1;
            } else if (normalizeElectricityType(spot?.electricityType) === '230v') {
                summary.type230 += 1;
            }

            summary.total += 1;
            return summary;
        }, { total: 0, type230: 0, type400: 0 });
    }, [spots]);

    const isConfirmedSpotStatus = (status) => status === SPOT_STATUS_PAYED || status === 'Maksettu';

    const isPaidSpotStatus = (status) => String(status ?? '').trim().toLowerCase() === String(SPOT_STATUS_PAYED ?? '').trim().toLowerCase()
        || String(status ?? '').trim().toLowerCase() === 'maksettu';

    const isOwnSpot = (spot) => Boolean(auth?.user?.userID && spot?.userID === auth.user.userID);

    const canRemoveOwnReservation = (spot) => isOwnSpot(spot)
        && String(spot?.status ?? '').trim().toLowerCase() !== String(SPOT_STATUS_FREE ?? '').trim().toLowerCase()
        && !isPaidSpotStatus(spot?.status);

    const canOpenOwnReservationDetails = (spot) => isOwnSpot(spot)
        && String(spot?.status ?? '').trim().toLowerCase() !== String(SPOT_STATUS_FREE ?? '').trim().toLowerCase();

    const selectedReservationSpot = useMemo(() => {
        if (selectedReservationSpotId == null || !Array.isArray(spots)) {
            return null;
        }

        return spots.find((spot) => spot.spotID === selectedReservationSpotId) ?? null;
    }, [selectedReservationSpotId, spots]);

    const selectedPublicSeller = useMemo(() => {
        if (!publicSellerMapMode || selectedPublicSellerId === 'all') {
            return null;
        }

        const matchedSeller = sellers.find((seller) => `${seller?.companyID ?? seller?.name}` === selectedPublicSellerId);

        if (matchedSeller) {
            return matchedSeller;
        }

        const routeSellerMatchesSelection = `${routeSellerCompanyID ?? routeSellerName}` === selectedPublicSellerId;

        if (routeSellerMatchesSelection && routeSellerSpotNames.length) {
            return {
                companyID: routeSellerCompanyID,
                name: routeSellerName,
                spotNames: routeSellerSpotNames,
            };
        }

        return null;
    }, [publicSellerMapMode, routeSellerCompanyID, routeSellerName, routeSellerSpotNames, selectedPublicSellerId, sellers]);

    const publicVisibleSpotIds = useMemo(() => {
        if (!publicSellerMapMode || !Array.isArray(spots)) {
            return new Set();
        }

        const sourceSpotNames = selectedPublicSeller
            ? selectedPublicSeller.spotNames ?? []
            : sellers.flatMap((seller) => seller?.spotNames ?? []);

        const visibleSpotNames = new Set(sourceSpotNames.map((spotName) => normalizeSpotIdentifier(spotName)));

        return new Set(
            spots
                .filter((spot) => isConfirmedSpotStatus(spot.status) && visibleSpotNames.has(normalizeSpotIdentifier(spot.name)))
                .map((spot) => spot.spotID)
        );
    }, [publicSellerMapMode, selectedPublicSeller, sellers, spots]);

    const publicHighlightedShapeKeys = useMemo(() => {
        if (!publicSellerMapMode || selectedPublicSellerId === 'all' || !Array.isArray(spots)) {
            return [];
        }

        return spots
            .filter((spot) => publicVisibleSpotIds.has(spot.spotID))
            .map((spot) => `${spot?.id ?? spot?.spotID ?? ''}`)
            .filter(Boolean);
    }, [publicSellerMapMode, publicVisibleSpotIds, selectedPublicSellerId, spots]);

    const setSpotInfoContent = (spot) => {
        if (!showSpotInfoPanel || !infoText.current) {
            return;
        }

        infoText.current.innerHTML = getInfoTable(spot);
    }

    const getSellerForSpot = (spotName) => {
        const normalizedSpotName = normalizeSpotIdentifier(spotName);

        return sellers.find((seller) =>
            seller?.spotNames?.some((sellerSpotName) => normalizeSpotIdentifier(sellerSpotName) === normalizedSpotName)
        );
    }

    const fetchSellers = async (showFetchError = true) => {
        if (sellerFetchPromiseRef.current) {
            return sellerFetchPromiseRef.current;
        }

        sellerFetchPromiseRef.current = apiRef.current
            .get('/getPayedReservation', {
                params: {
                    activityID: ACTIVITY_ID,
                    _ts: Date.now(),
                },
            })
            .then((response) => {
                const nextSellers = Array.isArray(response.data) ? response.data : [];
                setSellers(nextSellers);
                return nextSellers;
            })
            .catch((error) => {
                console.error('Error fetching sellers for map modal:', error);

                if (showFetchError) {
                    toast.error('Myyjätietojen haku epäonnistui');
                }

                return null;
            })
            .finally(() => {
                sellerFetchPromiseRef.current = null;
            });

        return sellerFetchPromiseRef.current;
    }

    const findSellerForSpot = (sellerList, spotName) => {
        const normalizedSpotName = normalizeSpotIdentifier(spotName);

        return sellerList.find((seller) =>
            seller?.spotNames?.some((sellerSpotName) => normalizeSpotIdentifier(sellerSpotName) === normalizedSpotName)
        );
    }

    const fetchSellerByCompanyId = async (companyID, showFetchError = true) => {
        if (!companyID || !isFinite(Number(companyID))) {
            return null;
        }

        try {
            const response = await apiRef.current.get('/getPublicSeller', {
                params: {
                    activityID: ACTIVITY_ID,
                    companyID: Number(companyID),
                    _ts: Date.now(),
                },
            });

            return response?.data ?? null;
        } catch (error) {
            if (error?.response?.status !== 404) {
                console.error('Error fetching seller by companyID:', error);

                if (showFetchError) {
                    toast.error('Myyjätietojen haku epäonnistui');
                }
            }

            return null;
        }
    }

    const containsCafeWord = (value) => String(value ?? '').toLowerCase().includes('kahvila');

    const isCafeSpot = (spot, seller = null) => {
        return containsCafeWord(spot?.name)
            || containsCafeWord(spot?.company)
            || containsCafeWord(seller?.name);
    }

    const openCafeModal = () => {
        setIsCafeImageError(false);
        setCafeImageZoom(1);
        setIsCafeModalOpen(true);
    }

    const closeCafeModal = () => {
        setIsCafeModalOpen(false);
        setIsCafeImageError(false);
        setCafeImageZoom(1);
    }

    const openProgramModal = () => {
        setIsProgramModalOpen(true);
    }

    const closeProgramModal = () => {
        setIsProgramModalOpen(false);
    }

    const adjustCafeImageZoom = (delta) => {
        setCafeImageZoom((currentZoom) => Math.max(1, Math.min(3, Number((currentZoom + delta).toFixed(2)))));
    }

    const resetCafeImageZoom = () => {
        setCafeImageZoom(1);
    }

    const addCafeHotspot = (targetLayer) => {
        if (!targetLayer || cafeHotspotShape) {
            return;
        }

        const hotspot = new Konva.Line({
            points: scalePolygonFromCentroid(CAFE_HOTSPOT_POINTS, CAFE_HOTSPOT_EXPAND_FACTOR),
            closed: true,
            fill: '#f59e0b',
            opacity: 0,
            stroke: '#f59e0b',
            strokeWidth: 2,
            hitStrokeWidth: 10,
            visible: true,
            listening: false,
            name: 'kahvio-hotspot',
        });

        hotspot.on('mouseover', function () {
            if (!publicSellerMapModeRef.current) {
                return;
            }

            document.body.style.cursor = 'pointer';
            hotspot.setOpacity(0.16);
            targetLayer.batchDraw();
        });

        hotspot.on('mouseout', function () {
            document.body.style.cursor = 'default';
            hotspot.setOpacity(0);
            targetLayer.batchDraw();
        });

        hotspot.on('click', function () {
            if (!publicSellerMapModeRef.current) {
                return;
            }

            openCafeModal();
        });

        hotspot.on('tap', function () {
            if (!publicSellerMapModeRef.current) {
                return;
            }

            openCafeModal();
        });

        cafeHotspotShape = hotspot;
        targetLayer.add(hotspot);
        hotspot.moveToTop();
        targetLayer.batchDraw();
    }

    const updateCafeHotspotState = () => {
        if (!cafeHotspotShape) {
            return;
        }

        const isPublicCafeMode = publicSellerMapMode && selectedPublicSellerId === 'all';
        cafeHotspotShape.setListening(isPublicCafeMode);
        cafeHotspotShape.setOpacity(0);

        if (!isPublicCafeMode) {
            document.body.style.cursor = 'default';
        }

        cafeHotspotShape.moveToTop();
        layer2?.batchDraw();
    }

    const openSellerModalForSpot = async (spot) => {
        if (publicSellerMapMode && isCafeSpot(spot)) {
            openCafeModal();
            return;
        }

        const directSeller = await fetchSellerByCompanyId(spot?.companyID, false);
        if (directSeller) {
            if (publicSellerMapMode && isCafeSpot(spot, directSeller)) {
                openCafeModal();
                return;
            }

            setSelectedSeller(directSeller);
            setIsSellerModalOpen(true);
            return;
        }

        // Always re-fetch before opening modal so edited company details are up to date.
        const refreshedSellers = await fetchSellers(false);
        let seller = null;

        if (Array.isArray(refreshedSellers)) {
            seller = findSellerForSpot(refreshedSellers, spot?.name);
        }

        if (!seller) {
            seller = getSellerForSpot(spot?.name);
        }

        if (!seller) {
            toast.info(`Paikan ${spot?.name ?? ''} myyjätietoja ei löytynyt.`.trim());
            return;
        }

        if (publicSellerMapMode && isCafeSpot(spot, seller)) {
            openCafeModal();
            return;
        }

        setSelectedSeller(seller);
        setIsSellerModalOpen(true);
    }

    const openOwnReservationModalForSpot = (spot) => {
        if (!spot?.spotID) {
            return;
        }

        setSelectedReservationSpotId(spot.spotID);
        setIsOwnReservationModalOpen(true);
    }

    const closeOwnReservationModal = () => {
        setIsOwnReservationModalOpen(false);
        setSelectedReservationSpotId(null);
        setIsRemovingReservation(false);
        setIsDeleteReservationConfirmOpen(false);
    }

    const requestRemoveSelectedOwnReservation = () => {
        if (!selectedReservationSpot) {
            return;
        }

        if (!canRemoveOwnReservation(selectedReservationSpot)) {
            toast.info('Maksettua varausta ei voi poistaa kartalta.');
            return;
        }

        setIsDeleteReservationConfirmOpen(true);
    }

    const removeSelectedOwnReservation = async () => {
        if (!selectedReservationSpot) {
            return;
        }

        setIsRemovingReservation(true);
        await unlockSpot(selectedReservationSpot, SPOT_STATUS_FREE);
        closeOwnReservationModal();
    }

    useEffect(() => {
        publicSellerMapModeRef.current = publicSellerMapMode;
    }, [publicSellerMapMode]);

    useEffect(() => {
        if (!publicSellerMapMode) {
            setSelectedPublicSellerId('all');
            return;
        }

        setSelectedPublicSellerId(routeSelectedSellerId);
    }, [publicSellerMapMode, routeFocusKey, routeSelectedSellerId]);

    useEffect(() => {
        if (publicSellerMapMode && location.state?.openCafeModal === true) {
            openCafeModal();
        }
    }, [location.state, publicSellerMapMode, routeFocusKey]);

    useEffect(() => {
        if (publicSellerMapMode && location.state?.openProgramModal === true) {
            openProgramModal();
        }
    }, [location.state, publicSellerMapMode, routeFocusKey]);

    const resetSellerSpotHighlight = (spotIds = []) => {
        spotIds.forEach((spotId) => {
            const highlightCircle = spotPoly[spotId + "highlight"];

            if (!highlightCircle) {
                return;
            }

            highlightCircle.hide();
            highlightCircle.setScale({ x: 1, y: 1 });
            highlightCircle.setOpacity(0);
            highlightCircle.setStrokeWidth(SELLER_HIGHLIGHT_STROKE_WIDTH);
            highlightCircle.setDash([14, 8]);
            highlightCircle.setShadowBlur(0);
            highlightCircle.setShadowColor('transparent');
        });

        layer2?.batchDraw();
    }

    const clearPendingViewportResize = () => {
        if (viewportResizeFrameRef.current != null) {
            window.cancelAnimationFrame(viewportResizeFrameRef.current);
            viewportResizeFrameRef.current = null;
        }

        if (viewportResizeTimeoutRef.current != null) {
            window.clearTimeout(viewportResizeTimeoutRef.current);
            viewportResizeTimeoutRef.current = null;
        }
    }

    const clearPendingSellerFocus = () => {
        if (sellerFocusFrameRef.current != null) {
            window.cancelAnimationFrame(sellerFocusFrameRef.current);
            sellerFocusFrameRef.current = null;
        }

        if (sellerFocusTimeoutRef.current != null) {
            window.clearTimeout(sellerFocusTimeoutRef.current);
            sellerFocusTimeoutRef.current = null;
        }
    }

    const stopSellerSpotHighlight = (spotIds = []) => {
        const targetSpotIds = spotIds.length ? spotIds : highlightedSpotIdsRef.current;

        clearPendingSellerFocus();

        if (sellerHighlightAnimationRef.current) {
            sellerHighlightAnimationRef.current.stop();
            sellerHighlightAnimationRef.current = null;
        }

        resetSellerSpotHighlight(targetSpotIds);
        highlightedSpotIdsRef.current = [];
    }

    useEffect(() => {
        const highlightedSpotIds = publicHighlightedShapeKeys;

        if (!publicSellerMapMode || !mapReady || !layer2) {
            stopSellerSpotHighlight();
            return;
        }

        if (selectedPublicSellerId === 'all' || highlightedSpotIds.length === 0) {
            stopSellerSpotHighlight();
            CenterAndScaleStage();
            stage?.batchDraw();
            return;
        }

        stopSellerSpotHighlight(highlightedSpotIds);
        highlightedSpotIdsRef.current = highlightedSpotIds;

        const highlightCircles = highlightedSpotIds
            .map((spotId) => spotPoly[spotId + 'highlight'])
            .filter(Boolean);

        if (!highlightCircles.length) {
            return;
        }

        const applySellerFocusState = () => {
            highlightedSpotIdsRef.current = highlightedSpotIds;
            focusStageOnSellerSpots(highlightedSpotIds);

            highlightCircles.forEach((highlightCircle) => {
                highlightCircle.moveToTop();
                highlightCircle.show();
                highlightCircle.setStroke('#dc2626');
                highlightCircle.setDash([14, 8]);
                highlightCircle.setShadowColor('#fca5a5');
                highlightCircle.setStrokeWidth(SELLER_HIGHLIGHT_STROKE_WIDTH);
                highlightCircle.setOpacity(0.95);
                highlightCircle.setScale({ x: 1.08, y: 1.08 });
            });

            layer2?.batchDraw();
        };

        applySellerFocusState();

        sellerFocusFrameRef.current = window.requestAnimationFrame(() => {
            applySellerFocusState();

            sellerFocusFrameRef.current = window.requestAnimationFrame(() => {
                applySellerFocusState();
            });
        });

        sellerFocusTimeoutRef.current = window.setTimeout(() => {
            applySellerFocusState();
        }, 180);

        sellerHighlightAnimationRef.current = new Konva.Animation((frame) => {
            const time = frame?.time ?? 0;
            const duration = 1500;
            const cycleProgress = (time % duration) / duration;
            const scale = 1.08 + cycleProgress * 0.42;
            const opacity = 0.95 - cycleProgress * 0.55;

            highlightCircles.forEach((highlightCircle) => {
                highlightCircle.setScale({ x: scale, y: scale });
                highlightCircle.setOpacity(Math.max(opacity, 0.25));
                highlightCircle.setShadowBlur(10 + cycleProgress * 16);
            });
        }, layer2);

        sellerHighlightAnimationRef.current.start();

        return () => {
            stopSellerSpotHighlight(highlightedSpotIds);
        };
    }, [mapReady, publicSellerMapMode, publicHighlightedShapeKeys, routeFocusKey, selectedPublicSellerId]);

    const getSpotShapeKey = (spot) => `${spot?.id ?? spot?.spotID ?? ''}`;

    const focusStageOnSellerSpots = (spotIds = []) => {
        if (!stage || !spotIds.length) {
            return;
        }

        const targetShapes = spotIds
            .map((spotId) => spotPoly[spotId])
            .filter(Boolean);

        if (!targetShapes.length) {
            return;
        }

        const bounds = targetShapes.reduce((accumulator, shape) => {
            const rect = shape.getClientRect({ skipTransform: true, skipStroke: false });

            return {
                minX: Math.min(accumulator.minX, rect.x),
                minY: Math.min(accumulator.minY, rect.y),
                maxX: Math.max(accumulator.maxX, rect.x + rect.width),
                maxY: Math.max(accumulator.maxY, rect.y + rect.height),
            };
        }, {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });

        if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.minY) || !Number.isFinite(bounds.maxX) || !Number.isFinite(bounds.maxY)) {
            return;
        }

        const focusPadding = 120;
        const boundsWidth = Math.max(bounds.maxX - bounds.minX, 100);
        const boundsHeight = Math.max(bounds.maxY - bounds.minY, 100);
        const baseScale = stage.minScale ?? stage.scaleX() ?? 1;
        const fittingScale = Math.min(
            width / (boundsWidth + focusPadding * 2),
            height / (boundsHeight + focusPadding * 2)
        );
        const preferredZoomScale = Math.min(baseScale * SELLER_FOCUS_SCALE_BOOST, SELLER_FOCUS_MAX_SCALE);
        const targetScale = Math.max(
            baseScale,
            Math.min(fittingScale, preferredZoomScale)
        );
        const focusCenterX = (bounds.minX + bounds.maxX) / 2;
        const focusCenterY = (bounds.minY + bounds.maxY) / 2;

        stage.setScaleX(targetScale);
        stage.setScaleY(targetScale);
        stage.setPosition({
            x: width / 2 - focusCenterX * targetScale,
            y: height / 2 - focusCenterY * targetScale,
        });

        relocateCanvas(false);
        stage.batchDraw();
    }

    const restoreSellerMapFocus = () => {
        const activeSpotIds = highlightedSpotIdsRef.current;

        if (publicSellerMapModeRef.current && activeSpotIds.length) {
            focusStageOnSellerSpots(activeSpotIds);
            return true;
        }

        return false;
    }

    let getSpot = (spotID) => {
        
        return spots.find(spot => spot.spotID === spotID);
        
    }    

    let addToReservation = (aPoly) => {

        const spot = getSpot(aPoly.attrs['id']);

        lockSpot(spot,auth.user.userID,SPOT_STATUS_LOCKED);
        //dispatch(addReservation(place)); 
    }

    let removeFromReservation = (aPoly) => {
        const spot = getSpot(aPoly.attrs['id']);

        unlockSpot(spot,SPOT_STATUS_FREE);
        //dispatch(removeReservation(place));   
    }



    //const imageRef = useRef(null);
    const canvasRef = useRef(null);
    infoText = useRef(null);

    useEffect(() => {
        if (hasFetchedSellersRef.current) {
            return;
        }

        hasFetchedSellersRef.current = true;

        fetchSellers();
    }, []);
    
    useEffect(() => {
        if (spots?.isLoading || !spots || spots.length <= 0 || stage || !canvasRef.current) {
            return;
        }

        setMapReady(false);
        stage = getStage(width, height);

        layer = new Konva.Layer();
        stage.add(layer);
        imageObj = getLMMapImage();
        layer2 = new Konva.Layer();

        const ctx = canvasRef.current.getContext("2d");
        ctx.drawImage(imageObj, 0, 0);

        setSpotInfoContent(null);

        const processedSpots = [];
        spots.forEach((place) => {
            if (!processedSpots.includes(place.spotID)) {
                addSpot(place, layer2);
                processedSpots.push(place.spotID);
            }
        });

        addCafeHotspot(layer2);

        stage.add(layer2);
        setMapReady(true);

        window.addEventListener("resize", scheduleViewportResize);
        window.addEventListener("orientationchange", scheduleViewportResize);
        window.visualViewport?.addEventListener("resize", scheduleViewportResize);
        scheduleViewportResize();
    }, [spots]);

    useEffect(() => {
        return () => {
            window.removeEventListener("resize", scheduleViewportResize);
            window.removeEventListener("orientationchange", scheduleViewportResize);
            window.visualViewport?.removeEventListener("resize", scheduleViewportResize);
            clearPendingViewportResize();
            stopSellerSpotHighlight();
            setMapReady(false);

            if (stage) {
                stage.destroy();
                stage = null;
            }

            layer = null;
            layer2 = null;
            imageObj = null;
            spotPoly = {};
            cafeHotspotShape = null;
        };
    }, []);

    useEffect(() => {
        updateCafeHotspotState();
    }, [publicSellerMapMode, selectedPublicSellerId, mapReady]);
  

    const spotsLoaded = () => {
        setLoaded(true);
    }

    useEffect(() => {
        const refreshSpots = (showError = false) => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }

            getOrders(spotsLoaded, { showError });
        };

        getOrders(spotsLoaded);

        const interval = setInterval(() => {
            refreshSpots(false);            
        }, 60000); // 60 seconds

        const handleVisibilityChange = () => refreshSpots(false);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }; // Cleanup on unmount
    },[]);      

    useEffect(() => {

        console.log("Get filtered spots");

        if (electricityManagementMode) {
            setFilteredSpots(spots.filter((spot) => isElectricityManagementSpot(spot)).map((spot) => spot.spotID));
            return;
        }

        if (publicSellerMapMode) {
            setFilteredSpots(Array.from(publicVisibleSpotIds));
            return;
        }

        let filtered = spots.filter(s => showSpots === "Kaikki" || (showSpots === "Vapaa" && (s.status === SPOT_STATUS_FREE || s.status === SPOT_STATUS_LOCKED)) || 
        (showSpots === "Ennakko" && s.status === SPOT_STATUS_PREORDERED) ||
        (showSpots === "Vahvistettu" && s.status === SPOT_STATUS_PAYED) ||
        (showSpots === "Varattu" && (s.status === SPOT_STATUS_ORDERED ||  s.status === SPOT_STATUS_INVOICED || s.status === "Maksu paikalla")) || (s.userID === auth.user?.userID && ((showSpots === "Valitut" && (s.status === SPOT_STATUS_LOCKED || s.status === SPOT_STATUS_PREORDERED)) || (showSpots === "Omat"))));

        filtered = sizeFilter.length
        ? filtered.filter((spot) => sizeFilter.some(item => item.size.includes(spot.size))).map(spot => spot.spotID)
        : filtered.map(spot => spot.spotID); // If no sizes selected, show all spots

        setFilteredSpots(filtered);

    },[electricityManagementMode, showSpots, sizeFilter, spots, publicSellerMapMode, publicVisibleSpotIds]);    

    function selectPlace(aPoly, aLayer, spot) {
        if (publicSellerMapMode) {
            if (isConfirmedSpotStatus(spot?.status)) {
                openSellerModalForSpot(spot);
            }
            return;
        }

        if (canOpenOwnReservationDetails(spot)) {
            openOwnReservationModalForSpot(spot);
            setSpotInfoContent(aPoly);
            return;
        }

        if (!isAuthenticated()) {
            navigate("/login");
            return;
        }

        if (spot?.status !== SPOT_STATUS_FREE) {
            if (isConfirmedSpotStatus(spot?.status)) {
                openSellerModalForSpot(spot);
            }

            return;
        }

        if (aPoly.selected) {
            aPoly.setFill(COLOUR_FREE);
            aPoly.selected = false;
            removeFromReservation(aPoly)
        } else {
            aPoly.setFill(COLOUR_OWN);
            aPoly.selected = true;
            addToReservation(aPoly);
        }
    }



    function getStage(aWitdth, aHeight) {

        Konva.hitOnDragEnabled = true;
    
        var stage = new Konva.Stage({
            container: 'mapContainer',
            preventDefault: false,
            height: aHeight,
            width: aWitdth,
            scaleX: 1,
            scaleY: 1,
            draggable: true
        });
    
        stage.on('wheel', function (event) {
            var touchPos = stage.getPointerPosition();
            var bx = stage.getX();
            var by = stage.getY();
            var bsx = stage.getScaleX();
            var bsy = stage.getScaleY();
            if (event.evt.deltaY < 0) {
                if (stage.getScaleX() > 10) {
                    return;
                }
                stage.setScaleX(stage.getScaleX() / scaleMultiplier);
                stage.setScaleY(stage.getScaleY() / scaleMultiplier);
                stage.draw();
            } else {
                var newScale = stage.getScaleX() * scaleMultiplier;
    
                if (newScale < stage.minScale) {
                    newScale = stage.minScale;
                }
                stage.setScaleX(newScale);
                stage.setScaleY(newScale);
                stage.draw();
            }
    
            var newX = touchPos.x - stage.getScaleX() * (touchPos.x - bx) / bsx;
            var newY = touchPos.y - stage.getScaleY() * (touchPos.y - by) / bsy;
    
    
            stage.setX(newX);
            stage.setY(newY);
            //relocateCanvas(false); 
            stage.draw();
        });
    
        stage.on("dragend", function(e) {        
            relocateCanvas(true);        
        });
    
        stage.on('touchmove', function (e) {
            e.evt.preventDefault();

            var touch1 = e.evt.touches[0];
            var touch2 = e.evt.touches[1];

            if (touch1 && touch2) {
                stage.multitouch = true;

                if (stage.isDragging()) {
                    stage.stopDrag();
                }

                var containerRect = stage.container().getBoundingClientRect();
                var point1 = {
                    x: touch1.clientX - containerRect.left,
                    y: touch1.clientY - containerRect.top
                };
                var point2 = {
                    x: touch2.clientX - containerRect.left,
                    y: touch2.clientY - containerRect.top
                };
                var center = getCenter(point1, point2);
                var dist = getDistance(point1, point2);

                if (!lastCenter || !lastDist) {
                    lastCenter = center;
                    lastDist = dist;
                    return;
                }

                var currentScale = stage.scaleX();
                var nextScale = currentScale * (dist / lastDist);
                nextScale = Math.max(stage.minScale ?? 0.2, Math.min(nextScale, 10));

                var pointTo = {
                    x: (center.x - stage.x()) / currentScale,
                    y: (center.y - stage.y()) / currentScale
                };

                var deltaX = center.x - lastCenter.x;
                var deltaY = center.y - lastCenter.y;

                stage.scale({ x: nextScale, y: nextScale });
                stage.position({
                    x: center.x - pointTo.x * nextScale + deltaX,
                    y: center.y - pointTo.y * nextScale + deltaY
                });

                relocateCanvas(false);
                stage.batchDraw();

                lastCenter = center;
                lastDist = dist;
            }
        });

        stage.on('touchend', function () {
            lastDist = 0;
            lastCenter = null;
            stage.multitouch = false;
        });
    
        stage.on('dbltap', function () {
            if (stage.multitouch) {
                stage.multitouch = false;
                return;
            }
            stage.setDraggable(!stage.getDraggable());                
        });
    
        //window.addEventListener('resize', resizeCanvas, false); TL_2025
        return stage;    
    }
    
    function getDistance(p1, p2) {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    }

    function getCenter(p1, p2) {
        return {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
        };
    }
    
    function scheduleViewportResize() {
        clearPendingViewportResize();
        resizeCanvas();

        viewportResizeFrameRef.current = window.requestAnimationFrame(() => {
            resizeCanvas();

            viewportResizeFrameRef.current = window.requestAnimationFrame(() => {
                resizeCanvas();
            });
        });

        viewportResizeTimeoutRef.current = window.setTimeout(() => {
            resizeCanvas();
        }, 180);
    }

    function getCurrentViewportState() {
        if (!stage) {
            return null;
        }

        const currentScale = stage.scaleX() || 1;

        return {
            scale: currentScale,
            centerX: (width / 2 - stage.x()) / currentScale,
            centerY: (height / 2 - stage.y()) / currentScale,
        };
    }

    function restoreViewportState(viewportState) {
        if (!stage || !viewportState || !imageObj?.width || !imageObj?.height) {
            return false;
        }

        const minScale = Math.min(height / imageObj.height, width / imageObj.width);
        const targetScale = Math.max(minScale, Math.min(viewportState.scale, 10));

        stage.minScale = minScale;
        stage.setScaleX(targetScale);
        stage.setScaleY(targetScale);
        stage.setPosition({
            x: width / 2 - viewportState.centerX * targetScale,
            y: height / 2 - viewportState.centerY * targetScale,
        });

        relocateCanvas(false);
        stage.batchDraw();
        return true;
    }

    function resizeCanvas() {        
        if(stage) {
            const previousViewportState = getCurrentViewportState();
            const viewportSize = getViewportSize();

            height = Math.max(viewportSize.height - 56, 320);
            width = viewportSize.width;
            stage.setWidth(width);
            stage.setHeight(height);

            if (!restoreSellerMapFocus() && !restoreViewportState(previousViewportState)) {
                CenterAndScaleStage();
                stage.draw();
            }
        }
    }
    
    function CenterAndScaleStage() {
        if (!stage || !imageObj?.height || !imageObj?.width) {
            return;
        }

        var scale1 = height / imageObj.height;
        var scale2 = width / imageObj.width;
    
        if (scale1 < scale2) {
            stage.setScaleX(scale1);
            stage.setScaleY(scale1);
            stage.setY(0);
    
            stage.setX((width - (imageObj.width) * scale1) / 2);
    
        } else {
            stage.setScaleX(scale2);
            stage.setScaleY(scale2);
            stage.setX(0);
            //stage.setY((height - (imageObj.height) * scale2) / 2);
            stage.setY(0);
        }
        stage.minScale = stage.getScaleX();
    }
    
    function getLMMapImage() {
        var image = new Image();
        image.onload = function () {
            var kartta = new Konva.Image({
                x: 0,
                y: 0,
                image: imageObj
            });
    
            if (imageObj) {
                CenterAndScaleStage();
            }
    
            // add the shape to the layer
            layer.add(kartta);

            if (!restoreSellerMapFocus()) {
                stage.batchDraw();
            }
        };
        //image.src = 'https://www.lcmynamaki.fi/wp-content/kartta.svg';
        image.src = 'https://lauri.lcmynamaki.fi/kartta.svg';
    
        return image;
    }
    
    
    
    
    function comparePaikat(p1, p2) {
    
        var p1_temp = p1.match(/(\d+)/);
        var p2_temp = p2.match(/(\d+)/);
    
        return parseInt(p1_temp) - parseInt(p2_temp);
    
    }

    function relocateCanvas(draw) {
        if (!stage || !imageObj?.width || !imageObj?.height) {
            return;
        }

        var stageW = stage.scaleX() * imageObj.width;        
        var stageH = stage.scaleX() * imageObj.height; 
        var update = false;
        if(stage.getX() + stageW <= width && stage.getX() <= 0) {
            if(stageW > width) {
                stage.setX(width-stageW);
            } else {
                stage.setX(0);
            }
            update = true;
        }
        if(stage.getY() + stageH <= height && stage.getY() <= 0) {
            if(stageH > height) {
                stage.setY(height-stageH);
            } else {
                stage.setY(0);
            }
            update = true;
        }
        if(stage.getX() + stageW > width && stage.getX() > 0 ) {
            if(stageW > width) {
                stage.setX(0);
            } else {
                stage.setX(width-stageW);
            }
            update = true;
        }
        if(stage.getY() + stageH > height && stage.getY() > 0) {
            if(stageH > height) {
                stage.setY(0);
            } else {
                stage.setY(height-stageH);
            }
            update = true;
        }
    
        if(update && draw) {
            stage.draw();
        }
    }
    
    
    /*
    function removeA(arr) {
        var what, a = arguments, L = a.length, ax;
        while (L > 1 && arr.length) {
            what = a[--L];
            while ((ax = arr.indexOf(what)) !== -1) {
                arr.splice(ax, 1);
            }
        }
        return arr;
    }
    */
    
    function getBgColour(status,userID) {

        if(auth.user?.userID === userID) {
            return COLOUR_OWN;       
        }

        switch(status) {
            case "Ennakkovaraus": return COLOUR_PRERESERVED;
            case "Varattu": return COLOUR_RESERVED;
            case "Laskutettu": return COLOUR_RESERVED;
            case "Maksettu": return  COLOUR_PAYED;     
            case SPOT_STATUS_LOCKED: 
                if(auth.user?.userID === userID) {
                    return COLOUR_OWN;       
                } else {
                    return COLOUR_PRERESERVED;
                }
            default: return COLOUR_FREE;
        }
    }

        
    
     

    function  addSpot(spot, aLayer) {
            
        let coordinates;
        let omaVaraus = false;
        const canOpenSellerInfo = isConfirmedSpotStatus(spot.status);
        
        if(auth?.user && spot.userID === auth.user?.userID) {            
            omaVaraus = true;
        }
        
    
        if (!Array.isArray(spot.coordinates)) {
            let tmp = spot.coordinates.replace("[", "");
            tmp = tmp.replace("]", "");
            coordinates = tmp.split(`,`).map(x => +x);
        } else {
            coordinates = spot.coordinates;
        }

        const xCoordinates = coordinates.filter((_, index) => index % 2 === 0);
        const yCoordinates = coordinates.filter((_, index) => index % 2 === 1);
        const minX = Math.min(...xCoordinates);
        const maxX = Math.max(...xCoordinates);
        const minY = Math.min(...yCoordinates);
        const maxY = Math.max(...yCoordinates);
        const spotCenterX = (minX + maxX) / 2;
        const spotCenterY = (minY + maxY) / 2;
        const highlightRadius = Math.max((maxX - minX) / 2, (maxY - minY) / 2) + SELLER_HIGHLIGHT_PADDING;
    
        var bgColor = getBgColour(spot.status,spot?.userID);
    
        if (omaVaraus) {
            bgColor = COLOUR_OWN;
        }
    
        var electricity = "Ei";
    
        if (spot.electric230v === "1" && spot.electric400v === "1") {
            electricity = "230V / 400V";
        } else if (spot.electric230v === "1") {
            electricity = "230V";
        } else if (spot.electric400v === "1") {
            electricity = "400V";
        }
    
        var aPoly = new Konva.Line({            
            id: spot.spotID,
            name: spot.name,
            koko: spot.size,
            hinta: spot.prize,
            points: coordinates,
            status: spot.status,
            electricity: electricity,
            fill: bgColor,
            stroke: 'black',
            strokeEnabled: false,
            opacity: 0.5,
            closed: true
        });
    
        if (omaVaraus) {
            aPoly.selected = true;
        }
    
        var aPolyLine = new Konva.Line({
            points: coordinates,
            fill: bgColor,
            stroke: 'black',
            fillEnabled: false,
            strokeWidth: 1,
            //opacity: 0.7,
            closed: true
        });

        var highlightCircle = new Konva.Circle({
            x: spotCenterX,
            y: spotCenterY,
            radius: highlightRadius,
            stroke: '#dc2626',
            strokeWidth: SELLER_HIGHLIGHT_STROKE_WIDTH,
            dash: [14, 8],
            opacity: 0,
            visible: false,
            listening: false,
        });

        const spotShapeKey = getSpotShapeKey(spot);

        spotPoly[spotShapeKey] = aPoly;
        spotPoly[spotShapeKey + "line"] = aPolyLine;
        spotPoly[spotShapeKey + "highlight"] = highlightCircle;
    
        if (!publicSellerMapMode && auth?.user && (spot.status === 'Vapaa' || omaVaraus)) {
            aPoly.on('mouseout', function () {
                //document.getElementById("info").innerHTML = "";
                document.body.style.cursor = "default"; 
                setSpotInfoContent(null);
                aPolyLine.setStroke('black');
                aLayer.draw();
            });
    
            aPoly.on('mouseover', function () {
                //selectedSpot = aPoly;
                //document.getElementById("infoText").innerHTML = aPoly.getName();
                document.body.style.cursor = "pointer";
                setSpotInfoContent(aPoly);
                /*
                "<table>" +
                    "<tr><td><strong>Paikka:</strong></td><td>" + aPoly.getName() + "</td></tr>" +
                    "<tr><td><strong>Koko:</strong></td><td>" + aPoly.attrs['koko'] + "</td></tr>" +
                    "<tr><td><strong>Hinta:</strong></td><td>" + aPoly.attrs['hinta'] + " €</td></tr>" +
                    "<tr><td><strong>Tila:</strong></td><td>" + aPoly.attrs['status'] + "</td></tr>" +
                    "</table>";
                    */
                //Paikka: { selectedSpot ? selectedSpot['hinta'] : '' } <br />
                //Koko: <br />
                //Hinta: <br />
                //document.getElementById("info").innerHTML = getInfoTable(aPoly.getName(), aPoly.attrs['koko'], aPoly.attrs['hinta']);
                aPolyLine.moveToTop();
                aPolyLine.setStroke('yellow');
                aLayer.draw();
            });
    
    
            aPoly.on('click', function () {
                selectPlace(aPoly, aLayer, spot);
            });
    
            aPoly.on('tap', function () {
                selectPlace(aPoly, aLayer, spot);
            });
    
            aPoly.on('touchmove', function () {                                
                setSpotInfoContent(aPoly);
                //document.getElementById("info").innerHTML = getInfoTable(aPoly.getName(), aPoly.attrs['koko'], aPoly.attrs['hinta']);
                aPolyLine.moveToTop();
                aPolyLine.setStroke('yellow');
                aLayer.draw();
                document.body.style.cursor = "pointer";
    
            });
    
            aPoly.on('touchend', function () {
                document.body.style.cursor = "default";
                //document.getElementById("info").innerHTML = "";
                setSpotInfoContent(null);
                aPolyLine.setStroke('black');
                aLayer.draw();
            });
    
        }

        if (canOpenSellerInfo) {
            aPoly.on('mouseover', function () {
                document.body.style.cursor = "pointer";
            });

            aPoly.on('mouseout', function () {
                document.body.style.cursor = "default";
            });

            aPoly.on('click', function () {
                if (!publicSellerMapMode && canOpenOwnReservationDetails(spot)) {
                    openOwnReservationModalForSpot(spot);
                    setSpotInfoContent(aPoly);
                    return;
                }

                openSellerModalForSpot(spot);
            });

            aPoly.on('tap', function () {
                if (!publicSellerMapMode && canOpenOwnReservationDetails(spot)) {
                    openOwnReservationModalForSpot(spot);
                    setSpotInfoContent(aPoly);
                    return;
                }

                openSellerModalForSpot(spot);
            });
        }
    
        aLayer.add(aPoly);
        aLayer.add(aPolyLine);
        aLayer.add(highlightCircle);
    }

    
    function getInfoTable(spot) {
    
        if(!auth?.user || !showSpotInfoPanel) {
            return null;
        }
        
        var table = "<table>" +
            "<tr><td><strong>Paikka:</strong></td><td>" + (spot ? spot.getName() : "") + "</td></tr>" +
            //"<tr><td><strong>Paikka:</strong></td><td>" + (spot ? spot.attrs['id'] : "") + "</td></tr>" +
            "<tr><td><strong>Koko:</strong></td><td>" + (spot ? spot.attrs['koko'] : "") + "</td></tr>" +
            "<tr><td><strong>Hinta:</strong></td><td>" + (spot ? spot.attrs['hinta'] + " €" : "") + "</td></tr>" +
            "<tr><td><strong>Tila:</strong></td><td>" + (spot ? spot.attrs['status'] : "") + "</td></tr>" +
            //"<tr><td><strong>Sähkö:</strong></td><td>" + (spot ? spot.attrs['electricity'] : "") + "</td></tr>" +
            //"<tr><td><input type='checkbox'  value='230V' " + (is230V() ? "checked" : "") + " onClick='(e) => {setElectricity230();}'>230V</input></td>" +
            //"<td><input type='checkbox'  value='400V' " + (is400V() ? "checked" : "") + ">400V</input></td></tr>" +
            "</table>";
        return table;
    }
    
      
    function postCall(path, params, method = 'post') {
     
        // The rest of this code assumes you are not using a library.
        // It can be made less wordy if you use one.
        const form = document.createElement('form');
        form.method = method;
        form.action = path;
     
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const hiddenField = document.createElement('input');
                hiddenField.type = 'hidden';
                hiddenField.name = key;
                hiddenField.value = params[key];
     
                form.appendChild(hiddenField);
            }
        }
     
        document.body.appendChild(form);
        form.submit();
    }               

    return (
        <div className='w-full m-0 overflow-x-hidden'>
            {publicSellerMapMode ? (
                <PublicSellerMapMenu
                    sellers={sellers}
                    selectedSellerId={selectedPublicSellerId}
                    onSellerChange={setSelectedPublicSellerId}
                    onCafeClick={openCafeModal}
                    onProgramClick={openProgramModal}
                />
            ) : electricityManagementMode ? (
                <div className='fixed top-14 left-0 right-0 z-30 flex flex-wrap items-center gap-3 border-b border-slate-200/70 bg-white/94 px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.1)] backdrop-blur md:px-6'>
                    <div>
                        <div className='text-[10px] font-bold uppercase tracking-[0.24em] text-blue-700/80'>Hallinta</div>
                        <div className='text-lg font-black text-slate-900'>Sähköpaikat</div>
                    </div>
                    <div className='text-sm text-slate-600'>Näytetään varatut paikat, joilla on sähkövaraus.</div>
                    <div className='ml-auto flex flex-wrap items-center gap-3 text-xs font-bold'>
                        <div className='rounded-md border border-amber-300 bg-amber-100 px-3 py-1 text-amber-900'>230V ({electricitySpotSummary.type230})</div>
                        <div className='rounded-md border border-teal-300 bg-teal-100 px-3 py-1 text-teal-900'>400V ({electricitySpotSummary.type400})</div>
                        <div className='rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-slate-700'>Yhteensä ({electricitySpotSummary.total})</div>
                    </div>
                </div>
            ) : (
                <SpotMenu spots={spots} showSpots={showSpots} setShowSpots={setShowSpots} sizeFilter={sizeFilter} setSizeFilter={setSizeFilter} isMapView={true} ></SpotMenu>
            )}
            <div className={`${auth.user && showSpotInfoPanel ? "fixed top-32 left-2 border-2 border-green-500 rounded-md z-20 bg-white/50 p-0" : "hidden"}`}>
                <div ref={infoText} className='classes.infoText' key="caption"></div>                
            </div>            
            <div className="fixed bottom-4 left-1/2 text-xs font-bold transform -translate-x-1/2 flex space-x-4 z-20">
                {electricityManagementMode ? (
                    <>
                        <div className="rounded-md border border-amber-300 bg-amber-100/90 p-2 text-amber-900">230V</div>
                        <div className="rounded-md border border-teal-300 bg-teal-100/90 p-2 text-teal-900">400V</div>
                    </>
                ) : (
                    <>
                        <div className="border-2 rounded-md z-30 bg-confirmed/60 p-1">Vahvistettu</div>
                        {!publicSellerMapMode ? <div className="border-2 rounded-md z-30 bg-free/60 p-1">Vapaa</div> : null}
                        {!publicSellerMapMode ? <div className="border-2 rounded-md z-30 bg-preorder/60 p-1">Ennakko</div> : null}
                        {!publicSellerMapMode ? <div className="border-2 rounded-md z-30 bg-ordered/60 p-1">Varattu</div> : null}
                    </>
                )}
            </div>
            <div id="mapContainer" className='mt-[56px] overflow-hidden'>

                <canvas ref={canvasRef} className='classes.canvas' />
                
                {layer2 && spots && spots.length > 0 ? 
                    
                    spots.forEach(spot => {
                        const canOpenSellerInfo = isConfirmedSpotStatus(spot.status);
                        const spotShapeKey = getSpotShapeKey(spot);
                        const ownReservation = canOpenOwnReservationDetails(spot);

                        if (publicSellerMapMode) {
                            const isSelectedSellerSpot = filteredSpots.includes(spot.spotID);

                            spotPoly[spotShapeKey]?.setListening(isSelectedSellerSpot && canOpenSellerInfo);
                            spotPoly[spotShapeKey]?.setFill(isSelectedSellerSpot ? COLOUR_PAYED : "white");
                            return;
                        }

                        if (electricityManagementMode) {
                            const isElectricSpot = filteredSpots.includes(spot.spotID);

                            spotPoly[spotShapeKey]?.setListening(false);
                            spotPoly[spotShapeKey]?.setFill(isElectricSpot ? getElectricityManagementColour(spot) : "white");
                            return;
                        }

                        if(!filteredSpots.includes(spot.spotID)) {
                            spotPoly[spotShapeKey]?.setListening(false);
                            spotPoly[spotShapeKey]?.setFill("white");
                        } else if(showSpots === "Kaikki" || (showSpots === "Vapaa" && (spot.status === SPOT_STATUS_FREE || 
                            (spot.status === SPOT_STATUS_LOCKED && ((auth && auth.user?.userID === spot?.userID) || !auth))))) {                            
                            spotPoly[spotShapeKey]?.setListening(canOpenSellerInfo || ownReservation || spot.status === SPOT_STATUS_FREE || (spot.status === SPOT_STATUS_LOCKED && spot.userID === auth.user?.userID));
                            spotPoly[spotShapeKey]?.setFill(getBgColour(spot.status,spot.userID));
                        } else if(auth &&  (spot.userID === auth.user?.userID && (showSpots === "Valitut" && spot.status === SPOT_STATUS_LOCKED) || 
                            (showSpots === "Omat" && spot.userID === auth.user?.userID))) {
                                spotPoly[spotShapeKey]?.setListening(ownReservation || spot.status === SPOT_STATUS_FREE || (spot.status === SPOT_STATUS_LOCKED && spot.userID === auth.user.userID));
                                spotPoly[spotShapeKey]?.setFill(getBgColour(spot.status,spot.userID));    
                        } else if((showSpots === "Varattu" && (spot.status === SPOT_STATUS_ORDERED ||  spot.status === SPOT_STATUS_INVOICED || spot.status === "Maksu paikalla"))) {
                            spotPoly[spotShapeKey]?.setListening(canOpenSellerInfo || ownReservation);
                            spotPoly[spotShapeKey]?.setFill(getBgColour(spot.status,spot.userID));  
                        } else if((showSpots === "Ennakko" && (spot.status === SPOT_STATUS_PREORDERED || (spot.status === SPOT_STATUS_LOCKED && spot.userID === auth.user?.userID)))) {
                            spotPoly[spotShapeKey]?.setListening(ownReservation);
                            spotPoly[spotShapeKey]?.setFill(getBgColour(spot.status,spot.userID));  
                        } else if((showSpots === "Vahvistettu" && (spot.status === SPOT_STATUS_PAYED))) {
                            spotPoly[spotShapeKey]?.setListening(canOpenSellerInfo || ownReservation);
                            spotPoly[spotShapeKey]?.setFill(getBgColour(spot.status,spot.userID));  
                        } else {
                            spotPoly[spotShapeKey]?.setListening(false);
                            spotPoly[spotShapeKey]?.setFill("white");
                        }
                }) :
                null
                }
            </div >
            <SellerInfoModal
                isOpen={isSellerModalOpen}
                seller={selectedSeller}
                onClose={() => {
                    setIsSellerModalOpen(false);
                    setSelectedSeller(null);
                }}
            />
            {isOwnReservationModalOpen && selectedReservationSpot ? (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700/80">Oma varaus</div>
                                <h2 className="mt-1 text-xl font-black text-slate-900">Paikka {selectedReservationSpot.name}</h2>
                            </div>
                            <button
                                type="button"
                                onClick={closeOwnReservationModal}
                                className="rounded-lg border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 hover:border-slate-400"
                            >
                                Sulje
                            </button>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Koko</div>
                                <div className="mt-1 font-semibold text-slate-900">{selectedReservationSpot.size || '-'}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Hinta</div>
                                <div className="mt-1 font-semibold text-slate-900">{selectedReservationSpot.fixedPrice ?? selectedReservationSpot.prize} €</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Yritys</div>
                                <div className="mt-1 font-semibold text-slate-900">{selectedReservationSpot.company || '-'}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Sähkö</div>
                                <div className="mt-1 font-semibold text-slate-900">{selectedReservationSpot.electricityType || 'Ei sähköä'}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 col-span-2">
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Tila</div>
                                <div className="mt-1 font-semibold text-slate-900">{selectedReservationSpot.status || '-'}</div>
                            </div>
                        </div>

                        {isPaidSpotStatus(selectedReservationSpot.status) ? (
                            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                                Maksettua varausta ei voi poistaa karttanäkymästä.
                            </div>
                        ) : null}

                        <div className="mt-5 flex flex-wrap justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    closeOwnReservationModal();
                                    navigate('/myyntipaikat-lista', { state: { initialShowSpots: 'Omat' } });
                                }}
                                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 hover:border-blue-300"
                            >
                                Muokkaa listassa
                            </button>
                            <button
                                type="button"
                                onClick={requestRemoveSelectedOwnReservation}
                                disabled={!canRemoveOwnReservation(selectedReservationSpot) || isRemovingReservation}
                                className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isRemovingReservation ? 'Poistetaan...' : 'Poista varaus'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            <ConfirmationModal
                isOpen={isDeleteReservationConfirmOpen}
                onClose={() => setIsDeleteReservationConfirmOpen(false)}
                onConfirm={removeSelectedOwnReservation}
                msg={`Haluatko varmasti poistaa varauksen paikasta ${selectedReservationSpot?.name ?? ''}?`}
            />
            {isCafeModalOpen ? (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-4">
                    <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl md:p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-black text-slate-900 md:text-xl">Kahvilan hinnasto</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => adjustCafeImageZoom(-0.25)}
                                    disabled={cafeImageZoom <= 1}
                                    className="rounded-lg border border-slate-300 px-2.5 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    -
                                </button>
                                <button
                                    type="button"
                                    onClick={resetCafeImageZoom}
                                    className="rounded-lg border border-slate-300 px-2.5 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400"
                                >
                                    {Math.round(cafeImageZoom * 100)}%
                                </button>
                                <button
                                    type="button"
                                    onClick={() => adjustCafeImageZoom(0.25)}
                                    disabled={cafeImageZoom >= 3}
                                    className="rounded-lg border border-slate-300 px-2.5 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    +
                                </button>
                                <button
                                    type="button"
                                    onClick={closeCafeModal}
                                    className="rounded-lg border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400"
                                >
                                    Sulje
                                </button>
                            </div>
                        </div>
                        {!isCafeImageError ? (
                            <div className="max-h-[70vh] overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
                                <img
                                    src={CAFE_MENU_IMAGE_SRC}
                                    alt="Kahvilan hinnasto"
                                    className="mx-auto block w-full cursor-zoom-in rounded-lg object-contain transition-transform duration-200"
                                    style={{ transform: `scale(${cafeImageZoom})`, transformOrigin: 'center top' }}
                                    onClick={() => setCafeImageZoom((currentZoom) => (currentZoom >= 2 ? 1 : 2))}
                                    onError={() => setIsCafeImageError(true)}
                                />
                            </div>
                        ) : (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                Kahvilan hinnastokuvaa ei löytynyt. Lisää tiedosto polkuun /public/kahvila_hinnasto.jpg.
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
            {isProgramModalOpen ? (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-4">
                    <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 px-5 py-4 text-white md:px-6">
                            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/80">Yleisölle</div>
                            <div className="mt-1 flex items-center justify-between gap-4">
                                <h2 className="text-xl font-black md:text-2xl">Päivän ohjelma</h2>
                                <button
                                    type="button"
                                    onClick={closeProgramModal}
                                    className="rounded-lg border border-white/60 bg-white/20 px-3 py-1 text-sm font-semibold text-white transition hover:bg-white/30"
                                >
                                    Sulje
                                </button>
                            </div>
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto bg-slate-50 px-4 py-4 md:px-6 md:py-5">
                            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:p-4">
                                {PUBLIC_EVENT_PROGRAM.map((entry, index) => (
                                    <div
                                        key={`${entry.time}-${entry.title}`}
                                        className={`grid grid-cols-[88px_1fr] items-start gap-3 py-2 ${index !== PUBLIC_EVENT_PROGRAM.length - 1 ? 'border-b border-slate-100' : ''}`}
                                    >
                                        <div className="inline-flex w-fit items-center rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-sm font-black text-indigo-900">
                                            {entry.time}
                                        </div>
                                        <div className="pt-1 text-sm font-semibold leading-relaxed text-slate-800 md:text-base">
                                            {entry.title}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            
        </div >
    );

}



export { MapPage as default };

